/*

author: Christophjer C. Alves Martins
This page is used to display the status of the automation machines and allow the user to trigger actions on them.

*/
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Automation as AutomationApi,
  AutomationClientActionsType,
} from "../../api/compute/Automation";
import { useAuth } from "../../hooks/useAuth";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
import Machine, { stateColors } from "./widget/Machine";

function Automation() {
  const auth = useAuth();
  const feedback = useFeedbackSystem();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authFailed, setAuthFailed] = useState<boolean>(false);
  const automationApi = useRef<AutomationApi | null>(null);
  const jsessionid = useMemo(
    () => auth.user.additionalCookies[0]?.value,
    [auth.user.additionalCookies],
  );
  const [summary, setSummary] = useState<any[]>([]);
  const [summaryMaster, setSummaryMaster] = useState<any>(null);
  const [summaryMachinesBarStatus, setSummarMachinesBarStatus] =
    useState<number>(-1);

  useEffect(() => {
    document.title = "Support - Automation";
  }, []);

  useEffect(() => {
    if (automationApi.current == null) {
      automationApi.current = new AutomationApi(jsessionid);
    }
  }, [jsessionid]);

  const getMachineLed = (summaryResponse) => {
    if (summaryResponse.length > 0) {
      summaryResponse.forEach((machine: any) => {
        if (machine != null) {
          machine.last_snapshot = new Date(machine.last_snapshot);
          machine.timestamp = new Date(machine.timestamp).toUTCString();
          machine.synch = !machine.update;
          if (
            machine.online.status === "OFFLINE" ||
            machine.online.status === "PERMANENT_OFFLINE"
          ) {
            machine.led = -1;
          } else if (
            machine.online.status === "ONLINE" &&
            (machine.process_status !== "READY" || machine.update)
          ) {
            machine.led = 0;
          } else if (machine.process_status === "RUNNING") {
            machine.led = 0;
          } else if (
            machine.online.status === "ONLINE" &&
            machine.process_status === "READY" &&
            !machine.update
          ) {
            machine.led = 1;
          } else {
            machine.led = -2;
          }
        }
      });

      let summaryBarStatus: number = -1;
      if (summaryResponse.length > 0) {
        summaryResponse.forEach((machine: any) => {
          if (machine && machine.led !== null) {
            if (machine.led === 1 && summaryBarStatus === -1) {
              summaryBarStatus = 1;
            } else if (machine.led === 0) {
              summaryBarStatus = 0;
            }
          }
        });
        setSummarMachinesBarStatus(summaryBarStatus);
      }
    }
    return summaryResponse;
  };

  const getMasterLed = (summaryMasterResponse) => {
    summaryMasterResponse.synch = !summaryMasterResponse.update;
    summaryMasterResponse.status = summaryMasterResponse.process_status;
    summaryMasterResponse.timestamp = new Date(
      summaryMasterResponse.timestamp,
    ).toUTCString();
    summaryMasterResponse.led = -2;
    if (
      summaryMasterResponse.status !== "READY" ||
      !summaryMasterResponse.synch
    ) {
      summaryMasterResponse.led = 0;
    } else if (summaryMasterResponse.status === "RUNNING") {
      summaryMasterResponse.led = 0;
    } else if (
      summaryMasterResponse.status === "READY" &&
      summaryMasterResponse.synch
    ) {
      summaryMasterResponse.led = 1;
    } else {
      summaryMasterResponse.led = -2;
    }
  };

  const onAuthSuccess = useCallback(async () => {
    try {
      const summaryResponse = await automationApi.current?.getSummary();
      const summaryMasterResponse =
        await automationApi.current?.getSummaryMaster();

      getMachineLed(summaryResponse);
      setSummary(summaryResponse);
      if (summaryMasterResponse) {
        getMasterLed(summaryMasterResponse);
        setSummaryMaster(summaryMasterResponse);
      }
    } catch (error) {
      feedback.show({
        type: "error",
        message: "Failed to fetch Automation summary data.",
      });
    } finally {
      setIsLoading(false);
      setAuthFailed(false);
    }
  }, [feedback]);

  const onAuthFailure = useCallback(() => {
    setAuthFailed(true);
    setIsLoading(false);
    feedback.show({
      type: "error",
      message: "Authentication failed for Automation API. Please try again.",
      autoHideDuration: 5000,
    });
  }, [feedback]);

  const getAuthenticated = useCallback(() => {
    // Check if jsessionid is available before making API calls
    // If jsessionid is not available, it means the user is not authenticated
    // in support app, and we should not attempt to login to automation
    if (!jsessionid) {
      feedback.show({
        type: "warning",
        message: "No jsessionid found in user additional cookies.",
      });
      return;
    }
    setIsLoading(true);
    automationApi.current
      ?.getAuthenticated()
      .then(onAuthSuccess)
      .catch(onAuthFailure);
  }, [feedback, jsessionid, onAuthFailure, onAuthSuccess]);

  useEffect(() => {
    getAuthenticated();
  }, [getAuthenticated]);

  const triggerAction = async (
    machineName: string,
    action: AutomationClientActionsType,
  ) => {
    const response = await automationApi.current?.triggerActions(
      machineName,
      action,
    );
    if (response.status === "OK") {
      const response = await automationApi.current?.getSummary();
      getMachineLed(response);
      setSummary(response);
    }
  };

  const fetchLogMaster = useCallback(async () => {
    try {
      const logMasterResponse = await automationApi.current?.getLogMAster();
      return logMasterResponse;
    } catch (error) {
      feedback.show({
        type: "error",
        message: "Failed to fetch Master log.",
      });
    }
  }, [feedback]);

  return (
    <Box
      className="AutomationPage"
      height={"100%"}
      width={"100%"}
      borderRadius={1}
      gap={1}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      overflow={"hidden"}
      
    >
      {isLoading && (
        <Box
          display={"flex"}
          width={"100%"}
          height={"100%"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          gap={2}
        >
          <CircularProgress color="secondary" />
          <Typography>Fetching Automation Data</Typography>
        </Box>
      )}

      {authFailed && !isLoading && (
        <Box
          display={"flex"}
          width={"100%"}
          height={"100%"}
          flexDirection={"column"}
          alignItems={"center"}
          gap={2}
          justifyContent={"center"}
        >
          <Typography color="error">
            Authentication failed for Automation API.
          </Typography>
          <Button onClick={getAuthenticated}>Retry</Button>
        </Box>
      )}

      {!isLoading && !authFailed && (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          width={"100%"}
          height={"100%"}
          overflow={"auto"}
        >
          <Box
            gap={1}
            borderRadius={1}
            border={"solid 1px " + stateColors[summaryMachinesBarStatus]}
          >
            <Typography
              p={1}
              variant="h6"
              sx={{ backgroundColor: stateColors[summaryMachinesBarStatus] }}
            >
              <strong>Server Status Summary</strong>
            </Typography>
            <Box p={1} display={"flex"} flexDirection={"column"} gap={1}>
              {summary &&
                summary.length > 0 &&
                summary.map(
                  (machine: any, index: number) =>
                    machine && (
                      <Machine
                        triggerAction={(action: AutomationClientActionsType) =>
                          triggerAction(machine?.name, action)
                        }
                        name={machine?.name}
                        isServing={machine?.isServing}
                        mode={
                          machine?.mode !== "LOCAL" ? "ORCHESTRATION" : "LOCAL"
                        }
                        lb={machine?.lb}
                        process_status={machine?.process_status}
                        timestamp={machine?.timestamp}
                        key={index}
                        LED={machine.led}
                        sync={machine.synch}
                      ></Machine>
                    ),
                )}
            </Box>
          </Box>
          {summaryMaster && (
            <>
              {/* <Divider orientation="horizontal" /> */}
              <Machine
                process_status={summaryMaster?.process_status}
                timestamp={summaryMaster?.timestamp}
                update={summaryMaster?.update}
                notifications={summaryMaster?.notifications}
                triggerAction={() => {}}
                LED={summaryMaster.led}
                logMasterHandler={() => fetchLogMaster()}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default Automation;
